import { AVERTISSEMENT_LAMPE, T } from '../domaine/libelles'
import { el, requis, vider } from './../ui/dom'
import { aller } from '../ui/routeur'
import {
  basculerTheme,
  definirLuminosite,
  luminositeCourante,
  LUMINOSITE_MAX,
  LUMINOSITE_MIN,
  themeCourant,
} from '../ui/theme'

/*
  Lampe rouge plein écran.

  Surface monochrome rouge dont on module la composante rouge :
  rgb(30 + 225·v, 6·v, 0). Aucune longueur d'onde courte, jamais.

  Le panneau de commande garde son propre fond sombre : à faible intensité,
  la surface devient presque noire et un texte posé dessus serait illisible.

  L'écran ne se quitte QUE par le bouton dédié. Pas de « toucher n'importe où
  pour sortir » : doigts sales et écran mouillé garantissent le tap fantôme,
  et sortir de la lampe par accident, c'est rallumer du blanc sur la plage.
*/

let verrou: WakeLockSentinel | null = null

function couleurSurface(luminosite: number): string {
  const v = Math.min(1, Math.max(0, luminosite / 100))
  return `rgb(${Math.round(30 + 225 * v)}, ${Math.round(6 * v)}, 0)`
}

async function poserVerrou(): Promise<boolean> {
  try {
    const api = (navigator as Navigator & { wakeLock?: WakeLock }).wakeLock
    if (!api) return false
    verrou = await api.request('screen')
    return true
  } catch {
    // Batterie faible, onglet en arrière-plan, API absente (iOS < 16.4).
    // C'est un confort, pas une fonction : on n'alerte pas l'utilisateur.
    return false
  }
}

async function libererVerrou(): Promise<void> {
  try {
    await verrou?.release()
  } catch {
    /* déjà relâché */
  }
  verrou = null
}

export function monterLampe(): () => void {
  const noeud = requis('lampe')
  let vivant = true

  const etat = el('div', { class: 'lampe__etat', text: T.ecranNonMaintenu })

  const curseur = el('input', {
    type: 'range',
    class: 'lampe__curseur',
    min: LUMINOSITE_MIN,
    max: LUMINOSITE_MAX,
    step: 5,
    value: luminositeCourante(),
    'aria-label': T.intensite,
  }) as HTMLInputElement

  const appliquer = (valeur: number) => {
    noeud.style.backgroundColor = couleurSurface(valeur)
  }

  curseur.addEventListener('input', () => {
    const valeur = Number(curseur.value)
    appliquer(valeur)
    void definirLuminosite(valeur)
  })

  /*
    Bascule du mode nuit pour TOUTE l'application.

    C'est le contrôle principal : l'onglet Lampe est l'endroit où l'on vient
    quand la nuit tombe, donc c'est là qu'on doit pouvoir passer l'interface
    en rouge. Un raccourci existe aussi sur l'heure de la barre d'état.
  */
  const bascule = el('button', { type: 'button', class: 'lampe__bascule' })
  const majBascule = () => {
    const nuit = themeCourant() === 'rouge'
    bascule.textContent = nuit ? T.passerEnNormal : T.passerEnNuit
    bascule.setAttribute('aria-pressed', nuit ? 'true' : 'false')
  }
  majBascule()
  bascule.addEventListener('click', () => {
    void basculerTheme().then(majBascule)
  })

  const sortie = el('button', {
    type: 'button',
    class: 'lampe__sortie',
    text: T.quitterLampe,
  })
  sortie.addEventListener('click', () => aller('patrouille'))

  const panneau = el('div', { class: 'lampe__panneau' }, [
    // Mise en garde reproduite mot pour mot. Toujours visible, non masquable.
    el('p', { class: 'lampe__avertissement', text: AVERTISSEMENT_LAMPE }),
    el('label', { class: 'lampe__label', text: T.intensite.toUpperCase() }),
    curseur,
    etat,
    bascule,
    sortie,
  ])

  vider(noeud)
  noeud.append(panneau)
  appliquer(luminositeCourante())
  noeud.hidden = false

  void poserVerrou().then((ok) => {
    if (vivant) etat.textContent = ok ? T.ecranMaintenuAllume : T.ecranNonMaintenu
  })

  // Android relâche le verrou dès que l'onglet passe en arrière-plan.
  const surVisibilite = () => {
    if (document.visibilityState === 'visible' && !noeud.hidden) {
      void poserVerrou().then((ok) => {
        if (vivant) etat.textContent = ok ? T.ecranMaintenuAllume : T.ecranNonMaintenu
      })
    }
  }
  document.addEventListener('visibilitychange', surVisibilite)

  curseur.focus()

  return () => {
    vivant = false
    document.removeEventListener('visibilitychange', surVisibilite)
    void libererVerrou()
    noeud.hidden = true
    noeud.style.backgroundColor = ''
    vider(noeud)
  }
}
